import React, { useMemo, useEffect, useState } from 'react'

import { SxProps } from '@mui/material'

import { useEntityActions } from 'redux/actions/Entities'
import { useQuery, useParams } from 'utils/hooks'
import { useEntity } from 'redux/hooks/Entities'

import { sortAlphabeticalAsc } from 'utils/sortHelper'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'

import { AiIconTip } from 'components_new/molecules/IconTip'
import Breadcrumbs from 'components_new/molecules/Breadcrumbs'
import ContentBox from 'components_new/molecules/ContentBox'
import DndList from 'components_new/molecules/DndList'
import DndListItem from 'components_new/molecules/DndList/DndListItem'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import FormSection from 'components_new/molecules/FormSection'
import NavigationItem from 'components_new/molecules/NavigationItem'
import SelectSearch from 'components_new/molecules/SelectSearch'

import { Context } from 'types/GlobalUserEvents'
import { IResourceRef } from 'types/GlobalEntities'
import { addRef } from 'utils/queryHandler'
import { useTranslation } from 'language/translator'

interface Props {
  sx?: SxProps
}

interface IEditColumn {
  ai_generated: boolean
  entity: IResourceRef
  id: string
  index: number
  title: string
}

const EntityProperties = React.memo((props: Props) => {
  const { sx } = props
  const { id } = useParams<{ id: string }>()
  const { tryPutEntityAttributes } = useEntityActions()
  const { lang, t } = useTranslation()

  const entity = useEntity(id)
  const { query } = useQuery()

  const edit = Boolean(query['edit'])

  const [showLockedColumns, setShowLockedColumns] = useState<boolean>(false)

  const [editedColumns, setEditedColumns] = useState<IEditColumn[]>(
    entity?.columns?.standard || []
  )

  // When navigation to another entity, reset edited columns
  useEffect(() => {
    setEditedColumns(entity?.columns?.standard || [])
  }, [id])

  const handleSaveColumns = (columns: IEditColumn[]) => {
    tryPutEntityAttributes(
      id,
      [...columns]
        .sort((a, b) => a.index - b.index)
        .map((item) => ({ id: item.id, hidden: false }))
    )
  }

  const handleChangeColumnOrder = (ids: string[]) => {
    const newColumns = ids.map((id, index) => {
      const element = editedColumns.find(
        (item) => item.id === id
      ) as IEditColumn

      return {
        ...element,
        index
      }
    })

    handleSaveColumns(newColumns)
    setEditedColumns(newColumns)
  }

  const handleAddColumn = (id: string) => {
    if (!entity) return

    const columnToAdd = entity.columns.options.find(
      (column) => column.id === id
    )

    if (!columnToAdd) return

    // find index of new column
    const lastIndex =
      editedColumns.length > 0
        ? Math.max(...editedColumns.map((c) => c.index))
        : -1

    const newColumn: IEditColumn = {
      ...columnToAdd,
      index: lastIndex + 1
    }

    const newColumns = [...editedColumns, newColumn]

    handleSaveColumns(newColumns)
    setEditedColumns(newColumns)
  }

  const handleDeleteColumn = (id: string) => {
    const newColumns = editedColumns.filter((column) => column.id !== id)

    handleSaveColumns(newColumns)
    setEditedColumns(newColumns)
  }

  const availableOptions = useMemo(() => {
    if (!entity) return []

    const editedIds = new Set(editedColumns.map((col) => col.id))

    return entity.columns.options
      .filter((option) => !editedIds.has(option.id))
      .sort((a, b) => {
        const byEntityTitle = sortAlphabeticalAsc(
          a.entity.title[lang],
          b.entity.title[lang]
        )

        if (byEntityTitle !== 0) return byEntityTitle

        return sortAlphabeticalAsc(a.title, b.title)
      })

      .map((column) => {
        return {
          id: column.id,
          endAdornment: column.ai_generated ? (
            <AiIconTip title={t('global', 'ai_generated')} />
          ) : undefined,
          title: `${column.entity.title[lang]} / ${column.title}`
        }
      })
  }, [entity?.columns, editedColumns])

  if (!entity) {
    return null
  }

  const lockedColumns = entity.columns.locked

  // success
  return (
    <Box sx={sx}>
      <FormSection
        helperText={t('entities', 'default_columns_description')}
        importance="recommended"
        title="Standard"
      >
        <Box>
          {entity.segment && !entity.segment_is_hidden ? (
            <DndListItem editable={false} sx={{ mb: 0.5 }} variant="filled">
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  height: 38,
                  pl: edit ? 4 : 1,
                  pr: 1
                }}
              >
                <Breadcrumbs
                  highlightCurrent={true}
                  items={[
                    {
                      icon: { name: 'Entity' },
                      sx: !edit
                        ? {
                            minWidth: 68,
                            width: 68
                          }
                        : undefined,
                      title: entity.title[lang] ?? '',
                      tooltip: !edit ? entity.title[lang] : undefined
                    },
                    {
                      icon: { name: 'Property' },
                      title: entity.segment
                    }
                  ]}
                  sx={{ flex: 1 }}
                />
                <Chip
                  icon={<Icon name="Private" />}
                  label={'Id'}
                  size="xsmall"
                />
              </Box>
            </DndListItem>
          ) : null}
          <DndList
            editable={edit}
            handleDelete={handleDeleteColumn}
            handleDragEnd={handleChangeColumnOrder}
            itemGap={4}
            itemHeight={38}
            items={editedColumns.map((column) => {
              const canNavigate = !edit && column.entity.id !== entity.id

              return {
                id: column.id,
                content: (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', height: 38 }}
                  >
                    <Breadcrumbs
                      highlightCurrent={true}
                      items={[
                        {
                          href: canNavigate
                            ? `/entities/${column.entity.id}`
                            : undefined,
                          icon: { name: 'Entity' },
                          sx: !edit
                            ? {
                                minWidth: 68,
                                width: 68
                              }
                            : undefined,
                          title: column.entity.title[lang],
                          tooltip: edit
                            ? undefined
                            : column.entity.id !== entity.id
                              ? `Gå till ${column.entity.title[lang]}`
                              : entity.title[lang],
                          search: canNavigate
                            ? addRef('', Context.FROM_COLUMNS)
                            : undefined
                        },
                        { icon: { name: 'Property' }, title: column.title }
                      ]}
                      sx={{ px: 1 }}
                    />
                    {column.ai_generated ? (
                      <AiIconTip title={t('global', 'ai_generated')} />
                    ) : null}
                  </Box>
                )
              }
            })}
            variant="filled"
          />
        </Box>
        <Collapse in={edit}>
          <SelectSearch
            closeOnSelect={true}
            disableClearable={true}
            multiple={false}
            onChange={(option) => handleAddColumn(option.id)}
            options={availableOptions}
            placeholder={`${t('entities', 'add_column')}...`}
            selected={null}
          />
        </Collapse>
      </FormSection>

      <FormSection
        actions={
          <Button
            onClick={() => setShowLockedColumns(!showLockedColumns)}
            size="small"
            variant="text"
          >
            {t('global', showLockedColumns ? 'hide' : 'show')}
          </Button>
        }
        helperText={t('entities', 'locked_columns_info')}
        title={t('global', 'locked')}
        titleAdornment={
          <Chip
            icon={<Icon name="Private" />}
            label={lockedColumns.length}
            size="xsmall"
          />
        }
      >
        <Collapse in={showLockedColumns} unmountOnExit={true}>
          {lockedColumns.length > 0 ? (
            lockedColumns.map((column) => (
              <ContentBox
                collapse={{
                  active: true,
                  defaultCollapsed: true,
                  unmountOnExit: true
                }}
                gutterBottom={true}
                header={{
                  divider: true,
                  title: column.title,
                  titleAdornment: (
                    <Chip
                      icon={<Icon name="Private" />}
                      label={`${column.metrics.length} ${t(
                        'metrics',
                        column.metrics.length === 1 ? 'metric' : 'metrics',
                        true
                      )}`}
                      size="xsmall"
                    />
                  )
                }}
                size="small"
                key={column.id}
              >
                {column.metrics.map((metric) => (
                  <NavigationItem
                    arrow={true}
                    key={metric.id}
                    item={{
                      href: `/metrics/${metric.id}`,
                      icon: {
                        name: 'Metric'
                      },
                      title: metric.title[lang],
                      search: addRef('', Context.FROM_COLUMNS)
                    }}
                  />
                ))}
              </ContentBox>
            ))
          ) : (
            <EmptyPlaceholder
              background={true}
              title={t('entities', 'no_locked_columns')}
            />
          )}
        </Collapse>
      </FormSection>
    </Box>
  )
})

export default EntityProperties
